import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/**
 * D1 — Was zwischen Frage und Antwort passiert, als Schrittliste.
 *
 * Gemessen liegen **2,5 bis 4,6 Sekunden** zwischen Absenden und Antwort. Darin wählt der
 * Router aus 82 Werkzeugen, ruft Abfragen, verkettet bis zu vier Runden und verdichtet.
 * Sichtbar war davon nichts — und weil man nicht sieht, *dass* gearbeitet wird, fühlen sich
 * 4 Sekunden wie 10 an.
 *
 * ## Zwei Regeln, die diese Komponente einhalten muss
 *
 * **1 · Sie darf nicht lügen.** Es gibt heute keinen Ereignisstrom vom Server (`POST
 * /Chatbot/Send` liefert eine fertige Antwort; die Werkzeug-Ereignisse existieren, gehen
 * aber nach Seq und nicht an den Client — das ist D4/Streaming und Backend-Arbeit). Die drei
 * Schritte hier sind deshalb **generisch** und nennen **keine Zahlen**. „63 Schichten
 * geladen" wäre erfunden. Sobald D4 steht, ersetzen echte Ereignisse diese Texte; die
 * Struktur bleibt.
 *
 * **2 · Sie darf nicht selbst wie Verzögerung wirken.** Unter {@link REVEAL_MS} bleibt es bei
 * der ruhigen Kugel — eine Schrittliste, die nach 400 ms erscheint und sofort wieder
 * verschwindet, ist Flackern. Erst ab ~1,2 s klappt sie auf.
 *
 * ## Warum jetzt Timer, wo vorher reines CSS stand
 *
 * Die alte Fassung war bewusst timerfrei (OnPush-freundlich). Sie konnte dafür auch nicht
 * unterscheiden, ob eine Antwort in 0,4 oder 4 Sekunden kommt — die Phasen liefen als
 * Endlosschleife weiter, unabhängig vom tatsächlichen Fortschritt. Zwei Zustände zu
 * unterscheiden geht ohne Zeitmessung nicht. Die Timer werden in `ngOnDestroy` abgeräumt,
 * und die Komponente steht ohnehin nur im DOM, solange `pending` läuft.
 */

/** Ab hier lohnt die Schrittliste — darunter ist sie Flackern. */
const REVEAL_MS = 1200;
/** Danach gilt Schritt 1 als erledigt (Frage verstanden, Werkzeug gewählt). */
const STEP_UNDERSTAND_MS = 1200;
/** Danach gilt Schritt 2 als erledigt (Daten geladen). */
const STEP_LOAD_MS = 2600;

@Component({
  selector: 'app-help-thinking-indicator',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ht" role="status" aria-live="polite">
      <span class="ht-sr">{{ 'HELP_CHAT.THINKING_ARIA' | translate }}</span>

      <div class="ht-orb" aria-hidden="true">
        <span class="ht-ping"></span><span class="ht-ping p2"></span>
        <span class="ht-ring"></span>
        <span class="ht-orbit"><b></b></span>
        <span class="ht-orbit o2"><b></b></span>
        <span class="ht-core"><img class="ht-logo" src="assets/img/logo-no-name.svg" alt="" /></span>
      </div>

      <!-- Kurze Antwort: nur die Kugel, keine Liste. -->
      <div class="ht-steps" *ngIf="revealed" aria-hidden="true">
        <div class="ht-title">{{ 'HELP_CHAT.PROGRESS.TITLE' | translate }}</div>
        <div class="ht-step" [ngClass]="stepClass(0)">
          <span class="ht-ico"><i [class]="stepIcon(0)"></i></span>
          {{ 'HELP_CHAT.PROGRESS.STEP_UNDERSTAND' | translate }}
        </div>
        <div class="ht-step" [ngClass]="stepClass(1)">
          <span class="ht-ico"><i [class]="stepIcon(1)"></i></span>
          {{ 'HELP_CHAT.PROGRESS.STEP_LOAD' | translate }}
        </div>
        <div class="ht-step" [ngClass]="stepClass(2)">
          <span class="ht-ico"><i [class]="stepIcon(2)"></i></span>
          {{ 'HELP_CHAT.PROGRESS.STEP_COMPOSE' | translate }}
        </div>
        <p class="ht-hint">{{ 'HELP_CHAT.PROGRESS.HINT' | translate }}</p>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        align-self: flex-start;
        width: 100%;
      }

      .ht {
        display: flex;
        align-items: flex-start;
        gap: var(--sp-3);
        max-width: 100%;
        padding: var(--sp-2) var(--sp-1);
      }

      /* sr-only: aus dem Layout entfernt, aber fuer Screenreader lesbar */
      .ht-sr {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
      }

      /* ── Orb ── */
      .ht-orb {
        position: relative;
        width: 44px;
        height: 44px;
        flex: none;
      }
      /* Transparenter Kern: das FixPlaner-Logo liegt direkt auf dem Hintergrund (kein weisser
         Badge -> sieht im Dark-Modus sauber aus), bleibt aufrecht & farbecht wie im Vollbild-Loader,
         waehrend Ring + Partikel drumherum rotieren -> Marken-Bezug zum F5-Ladebildschirm. */
      .ht-core {
        position: absolute;
        inset: 7px;
        z-index: 3;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
      }
      .ht-logo {
        width: 74%;
        height: 74%;
        object-fit: contain;
        display: block;
        /* Glow folgt der Logo-Form (kein Kreis) -> funktioniert auf hell wie dunkel */
        filter: drop-shadow(0 0 5px rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0.55));
        animation: ht-glow 1.8s ease-in-out infinite;
      }
      /* rotierender Energie-Ring (Conic-Gradient, per mask zum Ring beschnitten) */
      .ht-ring {
        position: absolute;
        inset: 0;
        z-index: 2;
        border-radius: 50%;
        background: conic-gradient(
          from 0deg,
          transparent 0%,
          rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0) 35%,
          var(--clr-primary-100, #4ccaf1) 72%,
          #dff7ff 92%,
          transparent 100%
        );
        -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3.5px));
        mask: radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3.5px));
        filter: drop-shadow(0 0 5px rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0.85));
        animation: ht-spin 1.5s linear infinite;
      }
      /* Radar-Pings */
      .ht-ping {
        position: absolute;
        inset: 5px;
        border-radius: 50%;
        border: 1.5px solid rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0.55);
        animation: ht-pingpulse 2.4s ease-out infinite;
      }
      .ht-ping.p2 {
        animation-delay: 1.2s;
      }
      /* umkreisende Partikel (zwei Orbits, gegenlaeufig) */
      .ht-orbit {
        position: absolute;
        inset: -3px;
        z-index: 1;
        animation: ht-spin 3.2s linear infinite;
      }
      .ht-orbit.o2 {
        inset: 3px;
        animation-duration: 2.2s;
        animation-direction: reverse;
      }
      .ht-orbit b {
        position: absolute;
        top: -3px;
        left: 50%;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #dff7ff;
        box-shadow: 0 0 9px var(--clr-primary-100, #4ccaf1), 0 0 3px #fff;
        transform: translateX(-50%);
      }
      .ht-orbit.o2 b {
        width: 4px;
        height: 4px;
        background: var(--clr-primary-100, #4ccaf1);
      }

      /* ── Schrittliste ── */
      .ht-steps {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 5px;
        /* Sanft auffalten statt hart erscheinen — sonst wirkt das Einblenden selbst
           wie ein Ruckeln in der Wartezeit. */
        animation: ht-reveal 220ms ease-out;
      }
      .ht-title {
        font-size: var(--fs-sm);
        font-weight: 700;
        color: var(--clr-text-100, #1d2733);
      }
      .ht-step {
        display: flex;
        align-items: center;
        gap: var(--sp-2);
        font-size: var(--fs-xs);
        color: var(--clr-text-300, #94a1ad);
      }
      .ht-ico {
        width: 14px;
        display: inline-flex;
        justify-content: center;
        font-size: var(--fs-xs);
      }
      .ht-step--done {
        color: var(--clr-text-200, #55606e);
      }
      .ht-step--done .ht-ico {
        color: var(--action-success, #067647);
      }
      .ht-step--active {
        color: var(--clr-text-100, #1d2733);
        font-weight: 600;
      }
      .ht-step--active .ht-ico {
        color: var(--clr-primary-200, #3e95be);
        animation: ht-blink 1.1s ease-in-out infinite;
      }
      .ht-hint {
        margin: 2px 0 0;
        font-size: var(--fs-xs);
        color: var(--clr-text-300, #94a1ad);
      }

      @keyframes ht-spin {
        to {
          transform: rotate(360deg);
        }
      }
      @keyframes ht-pingpulse {
        0% {
          transform: scale(0.55);
          opacity: 0.85;
        }
        100% {
          transform: scale(1.85);
          opacity: 0;
        }
      }
      @keyframes ht-glow {
        0%,
        100% {
          filter: drop-shadow(0 0 4px rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0.4));
        }
        50% {
          filter: drop-shadow(0 0 9px rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0.85));
        }
      }
      @keyframes ht-reveal {
        from {
          opacity: 0;
          transform: translateY(-3px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }
      @keyframes ht-blink {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.35;
        }
      }

      /* Bewegungsreduktion: ruhiger Zustand statt Animation. */
      @media (prefers-reduced-motion: reduce) {
        .ht-logo,
        .ht-ring,
        .ht-ping,
        .ht-orbit,
        .ht-steps,
        .ht-step--active .ht-ico {
          animation: none !important;
        }
      }
    `,
  ],
})
export class HelpThinkingIndicatorComponent implements OnInit, OnDestroy {
  /** Liste erst ab {@link REVEAL_MS} — darunter bleibt es bei der Kugel allein. */
  protected revealed = false;
  /** 0 = versteht, 1 = lädt, 2 = stellt zusammen. Schritte davor gelten als erledigt. */
  protected activeStep = 0;

  private readonly timers: ReturnType<typeof setTimeout>[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.timers.push(
      setTimeout(() => {
        this.revealed = true;
        this.cdr.markForCheck();
      }, REVEAL_MS),
      setTimeout(() => {
        this.activeStep = 1;
        this.cdr.markForCheck();
      }, STEP_UNDERSTAND_MS),
      setTimeout(() => {
        // Letzter Schritt bleibt aktiv, bis die Antwort da ist und die Komponente
        // aus dem DOM fliegt — er wird NIE als „erledigt" markiert. Ein Häkchen an
        // „Antwort zusammenstellen" ohne Antwort wäre genau die Art Lüge, die diese
        // Anzeige nicht erzählen darf.
        this.activeStep = 2;
        this.cdr.markForCheck();
      }, STEP_LOAD_MS),
    );
  }

  ngOnDestroy(): void {
    this.timers.forEach(clearTimeout);
  }

  protected stepClass(index: number): string {
    if (index < this.activeStep) return 'ht-step--done';
    if (index === this.activeStep) return 'ht-step--active';
    return 'ht-step--todo';
  }

  protected stepIcon(index: number): string {
    if (index < this.activeStep) return 'fa-solid fa-check';
    if (index === this.activeStep) return 'fa-solid fa-circle';
    return 'fa-regular fa-circle';
  }
}
